/**
 *
 * AppVerifyStepOne Component
 *
 */

import {
  Button,
  Enums,
  Skeleton,
  stylize,
} from "@envestnet/envreact-component-library";
import classnames from "classnames";
import React from "react";
import { mfaFlow } from "utils/enum";
import withIntl from "utils/withIntl";
import { StatusTypeEnum } from "./enums";
import messages from "./messages";
import styles, { Styles } from "./styles";
import { StatusType } from "./types";

const { ButtonStyle, ButtonSize } = Enums;

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below

  /** Description of this prop */
  onNext: Function;

  onCancel: Function;

  onCantScan: Function;

  loadQRCode: Function;

  qrCodeLink: string;

  status: StatusType;
}

export interface AppVerifyStepOneProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  currentLayout: mfaFlow;
}

const defaultProps: DefaultProps = {
  onNext: () => {},
  onCancel: () => {},
  onCantScan: () => {},
  loadQRCode: () => {},
  qrCodeLink: "",
  status: StatusTypeEnum.INITIAL,
};

type Props = InjectedProps & AppVerifyStepOneProps;

const AppVerifyStepOne = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    onNext,
    onCancel,
    onCantScan,
    loadQRCode,
    qrCodeLink,
    status,
    currentLayout,
  } = props;

  const [qrRendered, setQRRendered] = React.useState(false);

  React.useEffect(() => {
    loadQRCode({
      authenticatorType: "app",
      currentLayout,
    });
  }, [currentLayout]);

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div>
        <ol className={classnames(classes.orderedList)}>
          <li className="header-text-font-family">
            {formatMessage(messages.listItem1)}
          </li>
          <li className="header-text-font-family">
            {formatMessage(messages.listItem2)}
          </li>
        </ol>

        <img
          src={qrCodeLink}
          alt={formatMessage(messages.qrCode)}
          loading="lazy"
          onLoad={() => {
            setQRRendered(true);
          }}
          style={
            qrRendered ? { visibility: "visible" } : { visibility: "hidden" }
          }
        ></img>

        {!qrRendered && (
          <Skeleton
            height={119}
            width={122}
            variant="rect"
            style={{ margin: "auto" }}
          />
        )}

        <div>
          <Button
            buttonStyle={ButtonStyle.Link}
            buttonSize={ButtonSize.Regular}
            className={classnames(
              classes.linkButton,
              "header-text-font-family"
            )}
            name={formatMessage(messages.cantScanLabel)}
            onClick={() => onCantScan()}
            disabled={!qrRendered}
            aria-label="cant scan button"
          />
        </div>
        <div>
          <Button
            buttonStyle={ButtonStyle.Primary}
            buttonSize={ButtonSize.Regular}
            className={classnames(
              classes.nextButton,
              "no-border-radius",
              "header-text-font-family"
            )}
            name={formatMessage(messages.nextButtonLabel)}
            disabled={!qrRendered}
            loading={status === StatusTypeEnum.PROCESSING || !qrRendered}
            onClick={() => onNext()}
            aria-label="next button"
          />
        </div>

        <div>
          <Button
            buttonStyle={ButtonStyle.Link}
            buttonSize={ButtonSize.Regular}
            className={classnames(
              classes.linkButton,
              "header-text-font-family"
            )}
            name={formatMessage(messages.cancelButtonLabel)}
            onClick={() => onCancel()}
            disabled={!qrRendered}
            aria-label="cancel button"
          />
        </div>
      </div>
    </div>
  );
};

AppVerifyStepOne.defaultProps = defaultProps;

export default withIntl(stylize<Props, DefaultProps>(styles)(AppVerifyStepOne));
